import { fetch } from "../network";
import { proposedProjectConfigs } from "./configs";
import {
	EnvironmentalReportSerializer,
	ExistingFinancingSerializer,
	GeotechnicalReportSerializer,
	MarketingSerializer,
	OverviewSerializer,
	PermitSerializer,
	ProjectAppraisalSerializer,
	ScheduleSerializer,
	SecuredPropertyDetailsSerializer
} from "./serializer";

export function ProposedProjectController() {
	const environmentalReportSerializer = EnvironmentalReportSerializer();
	const existingFinancingSerializer = ExistingFinancingSerializer();
	const geotechnicalReportSerializer = GeotechnicalReportSerializer();
	const overviewSerializer = OverviewSerializer();
	const permitSerializer = PermitSerializer();
	const scheduleSerializer = ScheduleSerializer();
	const marketingSerializer = MarketingSerializer();
	const projectAppraisalSerializer = ProjectAppraisalSerializer();
	const securedPropertyDetailsSerializer = SecuredPropertyDetailsSerializer();

	const getEnvironmentalReport = async (opportunityId) => {
		const config = proposedProjectConfigs.get_environmental_report;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updateEnvironmentalReport = async (body) => {
		const config = proposedProjectConfigs.update_environmental_report;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		return new Promise((resolve, reject) => {
			environmentalReportSerializer
				.create(body)
				.then((serializedBody) => {
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => reject(error));
		});
	};

	const getExistingFinancing = async (opportunityId) => {
		const config = proposedProjectConfigs.get_existing_financing;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updateExistingFinancing = async (body) => {
		const config = proposedProjectConfigs.update_existing_financing;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		return new Promise((resolve, reject) => {
			existingFinancingSerializer
				.create(body)
				.then((serializedBody) => {
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	const getGeotechnicalReport = async (opportunityId) => {
		const config = proposedProjectConfigs.get_geotechnical_report;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updateGeotechnicalReport = async (body) => {
		const config = proposedProjectConfigs.update_geotechnical_report;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		return new Promise((resolve, reject) => {
			geotechnicalReportSerializer
				.create(body)
				.then((serializedBody) => {
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => reject(error));
		});
	};

	const getOverview = async (opportunityId) => {
		const config = proposedProjectConfigs.get_overview;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updateOverview = async (body) => {
		const config = proposedProjectConfigs.update_overview;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		return new Promise((resolve, reject) => {
			overviewSerializer
				.create(body)
				.then((serializedBody) => {
					delete serializedBody.loanType;
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => reject(error));
		});
	};

	const getPermits = async (opportunityId) => {
		const config = proposedProjectConfigs.get_permits;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updatePermits = async (body) => {
		const config = proposedProjectConfigs.update_permits;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		delete body.opportunityId;
		return new Promise((resolve, reject) => {
			permitSerializer
				.create(body)
				.then((serializedBody) => {
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => reject(error));
		});
	};

	const getSchedules = async (opportunityId) => {
		const config = proposedProjectConfigs.get_schedules;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updateSchedules = async (body) => {
		const config = proposedProjectConfigs.update_schedules;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		return new Promise((resolve, reject) => {
			scheduleSerializer
				.create(body)
				.then((serializedBody) => {
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => reject(error));
		});
	};

	const getMarketingStrategy = async (opportunityId) => {
		const config = proposedProjectConfigs.get_marketing_strategy;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updateMarketingStrategy = async (body) => {
		const config = proposedProjectConfigs.update_marketing_strategy;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		return new Promise((resolve, reject) => {
			marketingSerializer
				.create(body)
				.then((serializedBody) => {
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => reject(error));
		});
	};

	const getProjectAppraisal = async (opportunityId) => {
		const config = proposedProjectConfigs.get_project_appraisal;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updateProjectAppraisal = async (body) => {
		const config = proposedProjectConfigs.update_project_appraisal;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		return new Promise((resolve, reject) => {
			projectAppraisalSerializer
				.create(body)
				.then((serializedBody) => {
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => reject(error));
		});
	};

	const getSecuredPropertyDetails = async (opportunityId) => {
		const config = proposedProjectConfigs.get_secured_property_details;
		const endpoint = config.endpoint.replace("$id", opportunityId);
		return new Promise((resolve, reject) => {
			fetch(config.method, endpoint)
				.then((response) => resolve(response.data))
				.catch((error) => reject(error.response));
		});
	};

	const updateSecuredPropertyDetails = async (body) => {
		const config = proposedProjectConfigs.update_secured_property_details;
		const endpoint = config.endpoint.replace("$id", body.opportunityId);
		return new Promise((resolve, reject) => {
			securedPropertyDetailsSerializer
				.create(body)
				.then((serializedBody) => {
					fetch(config.method, endpoint, serializedBody)
						.then((response) => resolve(response.data))
						.catch((error) => reject(error.response));
				})
				.catch((error) => reject(error));
		});
	};

	return Object.freeze({
		getEnvironmentalReport,
		updateEnvironmentalReport,
		getExistingFinancing,
		updateExistingFinancing,
		getGeotechnicalReport,
		updateGeotechnicalReport,
		getOverview,
		updateOverview,
		getPermits,
		updatePermits,
		getSchedules,
		updateSchedules,
		getProjectAppraisal,
		updateProjectAppraisal,
		getSecuredPropertyDetails,
		updateSecuredPropertyDetails,
		getMarketingStrategy,
		updateMarketingStrategy
	});
}
